/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import * as go from 'gojs';
import { ReactDiagram } from 'gojs-react';

import {
  repositionGroups,
  setDescendantsVisibility,
} from '../../helpers/graph/visibility';
import {
  CurvedMultiLinkDigraphLayout,
  registerAllTemplates,
  startSkeletonAnimation,
  startTurnPaginationAnimation,
  stopSkeletonAnimation,
  stopTurnPaginationAnimation,
} from '../../lib/narrative-graph';
import {
  enrichLinkDataWithSpanDetails,
  enrichNodeDataWithSpanDetails,
  projectTurnChildren,
  projectTurnGraph,
  transformTurnsResponse,
  TURN_X,
  TURN_START_Y,
  GROUP_X_OFFSET,
  TURN_VERTICAL_SPACING,
} from '../../utils/graph';
import DetailPanel from './detail-panel';

export interface NarrativeSpanDetails {
  traceSpanDetails: Record<string, { spans: any[] }>;
}

/**
 * Returns true only when the selected element has content that DetailPanel
 * will actually render. Mirrors the boolean guards inside DetailPanel so the
 * panel is never opened for empty elements (start/end/skeleton nodes,
 * TURN_SEQUENCE edges, unloaded turn nodes, tool/agent nodes with null values).
 */
function hasDisplayableData(type: 'node' | 'link', data: any): boolean {
  if (!data) return false;

  if (type === 'node') {
    const category: string | undefined = data.category;
    if (category === 'tool') {
      return (
        data.description?.value != null ||
        data.tray?.['scope.agentic.invocation'] != null ||
        data.tray?.['scope.agentic.session'] != null ||
        data.tray?.['scope.agentic.turn'] != null
      );
    }
    if (category === 'agent') {
      return (
        data.systemPrompt?.value != null ||
        data.tray?.['scope.agentic.invocation'] != null ||
        data.tray?.['scope.agentic.session'] != null ||
        data.tray?.['scope.agentic.turn'] != null
      );
    }
    if (category === 'turn') return data.tray?.edgeType === 'INVOKES';
    return false; // start, end, skeleton, unknown → nothing to show
  }

  if (type === 'link') {
    const edgeType: string | undefined = data.edgeType?.toUpperCase();
    return (
      edgeType === 'INVOKES' ||
      edgeType === 'DELEGATES_TO' ||
      edgeType === 'USES_TOOL' ||
      edgeType === 'HANDOFF'
    );
  }

  return false;
}

const initDiagram = () => {
  const $ = go.GraphObject.make;
  go.Diagram.licenseKey = `${process.env.NEXT_PUBLIC_GOJS_LICENSE}`;

  const diagram = $(go.Diagram, {
    layout: $(go.Layout), // No automatic layout for main diagram
    'undoManager.isEnabled': false,
    // Clicking the diagram background clears selection (closes the detail panel)
    click: (e: go.InputEvent) => {
      e.diagram.clearSelection();
    },
  });

  diagram.model = new go.GraphLinksModel({
    nodeKeyProperty: 'key',
    linkKeyProperty: 'id',
    linkFromPortIdProperty: 'fromPort',
    linkToPortIdProperty: 'toPort',
  });

  // Group template for horizontal layout of agents/tools per turn
  diagram.groupTemplate = $(
    go.Group,
    'Auto',
    {
      layout: new CurvedMultiLinkDigraphLayout({
        direction: 0, // 0 = right (horizontal)
        setsPortSpots: false,
        columnSpacing: 50,
        layerSpacing: 80,
        alignOption: go.LayeredDigraphAlign.All,
      }),
      locationSpot: go.Spot.TopLeft,
      locationObjectName: 'GROUP_PLACEHOLDER',
    },
    new go.Binding('location', 'location', go.Point.parse, go.Point.stringify)
  ).add(
    $(go.Shape, 'Rectangle', {
      fill: 'transparent',
      stroke: 'transparent',
      strokeWidth: 0,
      pickable: false,
    }),
    $(go.Placeholder, { name: 'GROUP_PLACEHOLDER', padding: 20 })
  );

  // Register all node templates (turn, end, agent, tool) + link template
  registerAllTemplates(diagram, $);

  // Apply initial visibility state after diagram is loaded
  diagram.addDiagramListener('InitialLayoutCompleted', (e) => {
    const diagram = e.diagram;
    diagram.startTransaction('initial visibility');
    diagram.nodes.each((node) => {
      if (
        !(node instanceof go.Group) &&
        node.data.category === 'turn' &&
        node.data.isExpanded === false
      ) {
        setDescendantsVisibility(node, false);
      }
    });
    // Reposition groups based on initial expanded state
    repositionGroups(diagram);
    diagram.commitTransaction('initial visibility');
  });

  return diagram;
};

interface NarrativeGraphProps {
  turnsData?: any;
  turnChildrenData?: Record<string, any>;
  turnChildrenLoading?: Record<string, boolean>;
  onExpandTurn?: (turnNodeId: string, traceId: string) => void;
  questionsData?: any[];
  iconMap?: Record<string, string>;
  height?: string;
  className?: string;
  isVisible?: boolean;
  spanDetails?: NarrativeSpanDetails;
  isFetchingMoreTurns?: boolean;
}

export default function NarrativeGraph({
  turnsData,
  turnChildrenData = {},
  turnChildrenLoading = {},
  onExpandTurn,
  questionsData = [],
  iconMap = {},
  height = '600px',
  className = 'narrative-graph',
  isVisible = true,
  spanDetails,
  isFetchingMoreTurns = false,
}: NarrativeGraphProps) {
  const diagramRef = useRef<ReactDiagram>(null);
  const prevIsVisible = useRef(isVisible);
  // Track which turn children have had visibility/positioning applied
  const mergedChildrenRef = useRef<Set<string>>(new Set());
  // Keep a stable ref to onExpandTurn so the model listener always calls the latest callback
  const onExpandTurnRef = useRef(onExpandTurn);
  onExpandTurnRef.current = onExpandTurn;

  // Selected node or edge for the detail side panel
  const [selectedElement, setSelectedElement] = useState<{
    type: 'node' | 'link';
    data: any;
  } | null>(null);
  const setSelectedElementRef = useRef(setSelectedElement);

  // Close handler: clears React state AND GoJS selection (resets visual styling)
  const handleClose = useCallback(() => {
    setSelectedElement(null);
    const diagram = diagramRef.current?.getDiagram();
    if (diagram) diagram.clearSelection();
  }, []);

  // Reset merged children tracking when turnsData changes (e.g., pagination)
  useEffect(() => {
    mergedChildrenRef.current.clear();
  }, [turnsData]);

  useEffect(() => {
    // When transitioning from hidden to visible, tell GoJS to recalculate
    if (isVisible && !prevIsVisible.current) {
      const diagram = diagramRef.current?.getDiagram();
      if (diagram) {
        diagram.requestUpdate();
      }
    }
    prevIsVisible.current = isVisible;
  }, [isVisible]);

  // Listen for TurnExpandRequested events from the diagram model (fired by turn-node click handler)
  // Uses a ref for the callback so we attach the listener only once (no stale closure issues)
  useEffect(() => {
    // Retry briefly until the diagram is ready (ReactDiagram renders async)
    let attempts = 0;
    const maxAttempts = 20;
    const intervalMs = 100;

    const tryAttach = () => {
      const diagram = diagramRef.current?.getDiagram();
      if (!diagram) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryAttach, intervalMs);
        }
        return;
      }

      const listener = (e: go.ChangedEvent) => {
        if (
          e.change === go.ChangeType.Transaction &&
          e.propertyName === 'TurnExpandRequested' &&
          e.newValue
        ) {
          const { turnNodeId, traceId } = e.newValue;
          if (turnNodeId && traceId && onExpandTurnRef.current) {
            onExpandTurnRef.current(turnNodeId, traceId);
          }
        }
      };

      diagram.addModelChangedListener(listener);
      // Store cleanup
      cleanupRef.current = () => {
        diagram.removeModelChangedListener(listener);
      };
    };

    const cleanupRef = { current: () => {} };
    tryAttach();

    return () => {
      cleanupRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for ChangedSelection diagram events and sync to React state
  // so the detail side panel knows what is currently selected.
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 20;
    const intervalMs = 100;

    const tryAttach = () => {
      const diagram = diagramRef.current?.getDiagram();
      if (!diagram) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryAttach, intervalMs);
        }
        return;
      }

      const selectionListener = () => {
        const sel = diagram.selection;
        if (sel.count === 1) {
          const part = sel.first();
          if (part instanceof go.Link) {
            if (hasDisplayableData('link', part.data)) {
              setSelectedElementRef.current({ type: 'link', data: part.data });
            } else {
              setSelectedElementRef.current(null);
              diagram.clearSelection();
            }
          } else if (part instanceof go.Node && !(part instanceof go.Group)) {
            if (hasDisplayableData('node', part.data)) {
              setSelectedElementRef.current({ type: 'node', data: part.data });
            } else {
              setSelectedElementRef.current(null);
              diagram.clearSelection();
            }
          } else {
            setSelectedElementRef.current(null);
          }
        } else {
          setSelectedElementRef.current(null);
        }
      };

      diagram.addDiagramListener('ChangedSelection', selectionListener);
      cleanupSelRef.current = () => {
        diagram.removeDiagramListener('ChangedSelection', selectionListener);
      };
    };

    const cleanupSelRef = { current: () => {} };
    tryAttach();

    return () => {
      cleanupSelRef.current();
    };
  }, []);

  // Manage the pagination pulse on the last loaded turn. Wait a frame so the
  // ReactDiagram merge has applied isFetchingMore: true and the Shape exists.
  useEffect(() => {
    const diagram = diagramRef.current?.getDiagram();
    if (!diagram) return;

    if (isFetchingMoreTurns) {
      const handle = requestAnimationFrame(() => {
        const d = diagramRef.current?.getDiagram();
        if (d) startTurnPaginationAnimation(d);
      });
      return () => {
        cancelAnimationFrame(handle);
        const d = diagramRef.current?.getDiagram();
        if (d) stopTurnPaginationAnimation(d);
      };
    }

    stopTurnPaginationAnimation(diagram);
    return undefined;
  }, [isFetchingMoreTurns, turnsData]);

  // Manage skeleton pulsing animation and positioning based on loading state
  useEffect(() => {
    const diagram = diagramRef.current?.getDiagram();
    if (!diagram) return;

    const loadingTurnIds = Object.entries(turnChildrenLoading)
      .filter(([, loading]) => loading)
      .map(([turnNodeId]) => turnNodeId);

    if (loadingTurnIds.length > 0) {
      // Wait for ReactDiagram to merge skeleton nodes, then reposition and animate.
      let attempts = 0;
      const maxAttempts = 10;
      const intervalMs = 50;

      const tryPosition = () => {
        const d = diagramRef.current?.getDiagram();
        if (!d) return;

        let allFound = true;
        for (const turnNodeId of loadingTurnIds) {
          const skeletonGroupKey = `skeleton_group_${turnNodeId}`;
          if (!d.findNodeForKey(skeletonGroupKey)) {
            allFound = false;
            break;
          }
        }

        if (allFound) {
          d.startTransaction('position skeleton groups');
          repositionGroups(d);
          d.commitTransaction('position skeleton groups');
          startSkeletonAnimation(d);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryPosition, intervalMs);
        }
      };

      requestAnimationFrame(tryPosition);
    } else {
      stopSkeletonAnimation(diagram);
    }
  }, [turnChildrenLoading]);

  // Apply visibility and positioning for newly added children.
  // Children are included in nodeDataArray/linkDataArray props (computed below)
  // so ReactDiagram handles adding/keeping them in the model. This effect only
  // runs post-add tasks: setting visibility based on parent turn's expanded state
  // and repositioning groups.
  useEffect(() => {
    const diagram = diagramRef.current?.getDiagram();
    if (!diagram || !turnChildrenData) return;

    const pendingTurnIds = Object.entries(turnChildrenData)
      .filter(([turnNodeId, childrenData]) => {
        return childrenData && !mergedChildrenRef.current.has(turnNodeId);
      })
      .map(([turnNodeId]) => turnNodeId);

    if (pendingTurnIds.length === 0) return;

    // ReactDiagram merges data in componentDidUpdate, which may run after this
    // effect. Use a short polling interval to wait for nodes to appear.
    let attempts = 0;
    const maxAttempts = 10;
    const intervalMs = 50;

    const tryApply = () => {
      const currentDiagram = diagramRef.current?.getDiagram();
      if (!currentDiagram) return;

      let allProcessed = true;

      pendingTurnIds.forEach((turnNodeId) => {
        if (mergedChildrenRef.current.has(turnNodeId)) return;

        const groupKey = `group_${turnNodeId}`;
        if (!currentDiagram.findNodeForKey(groupKey)) {
          allProcessed = false;
          return; // Not yet in diagram, retry
        }

        currentDiagram.startTransaction('apply children visibility');

        const turnNodeObj = currentDiagram.findNodeForKey(turnNodeId);
        if (turnNodeObj) {
          const expanded = turnNodeObj.data.isExpanded;
          setDescendantsVisibility(turnNodeObj, expanded);
        }

        repositionGroups(currentDiagram);
        currentDiagram.commitTransaction('apply children visibility');

        mergedChildrenRef.current.add(turnNodeId);
      });

      if (!allProcessed && attempts < maxAttempts) {
        attempts++;
        setTimeout(tryApply, intervalMs);
      }
    };

    // Defer first attempt to let ReactDiagram merge
    requestAnimationFrame(tryApply);
  }, [turnChildrenData, questionsData, iconMap]);

  // Build the data arrays for ReactDiagram, including fetched children.
  // Including children in the props ensures ReactDiagram's model merge does NOT
  // remove them across re-renders (previously, children added via direct model
  // manipulation were dropped whenever a new re-render triggered a merge).
  let nodeDataArray: any[] = [];
  let linkDataArray: any[] = [];

  if (turnsData) {
    const transformedTurns = transformTurnsResponse(turnsData);
    const projected = projectTurnGraph(
      transformedTurns,
      questionsData,
      iconMap
    );

    // Read current expansion state from the GoJS diagram to preserve it across
    // re-renders. Without this, projectTurnGraph always sets only the first turn
    // as expanded, and ReactDiagram's merge overwrites the user's click state.
    const diagram = diagramRef.current?.getDiagram();
    const currentExpandState = new Map<string, boolean>();
    if (diagram) {
      diagram.nodes.each((node) => {
        if (!(node instanceof go.Group) && node.data.category === 'turn') {
          currentExpandState.set(node.key as string, !!node.data.isExpanded);
        }
      });
    }

    nodeDataArray = projected.nodeDataArray.map((n: any) => {
      if (n.category === 'turn' && currentExpandState.size > 0) {
        return {
          ...n,
          isExpanded: currentExpandState.get(n.key) ?? n.isExpanded,
        };
      }
      return n;
    });
    linkDataArray = [...projected.linkDataArray];

    // Append fetched children so they persist in the ReactDiagram model
    Object.entries(turnChildrenData).forEach(([turnNodeId, childrenData]) => {
      if (!childrenData) return;
      try {
        const turnNode = projected.nodeDataArray.find(
          (n: any) => n.key === turnNodeId
        );
        const turnY = turnNode?.location
          ? parseFloat(turnNode.location.split(' ')[1])
          : undefined;
        const {
          nodeDataArray: childNodes,
          linkDataArray: childLinks,
          turnNodeTray,
        } = projectTurnChildren(
          turnNodeId,
          childrenData,
          questionsData,
          iconMap,
          turnY
        );
        nodeDataArray.push(...childNodes);
        linkDataArray.push(...childLinks);
        // Update the existing turn node's tray data in-place
        if (turnNodeTray) {
          const turnNodeIdx = nodeDataArray.findIndex(
            (n: any) => n.key === turnNodeId
          );
          if (turnNodeIdx !== -1) {
            nodeDataArray[turnNodeIdx] = {
              ...nodeDataArray[turnNodeIdx],
              tray: turnNodeTray,
            };
          }
        }
      } catch (err) {
        console.error(
          `[NarrativeGraph] Error projecting children for turn ${turnNodeId}:`,
          err
        );
      }
    });

    // Add ghost placeholder nodes for turns that are expanded and loading.
    // Mimics: turn → 1 agent → 2 child nodes
    Object.entries(turnChildrenLoading).forEach(([turnNodeId, isLoading]) => {
      if (!isLoading) return;
      // Don't show placeholder if real children already loaded
      if (turnChildrenData[turnNodeId]) return;

      const turnNode = nodeDataArray.find((n: any) => n.key === turnNodeId);
      if (!turnNode || !turnNode.isExpanded) return;

      // Use the projected turn node Y (same approach as projectTurnChildren)
      // so the skeleton appears on the same line as the turn node.
      // repositionGroups() will keep it in sync at runtime.
      const turnY = turnNode.location
        ? parseFloat(turnNode.location.split(' ')[1])
        : 100;

      const groupKey = `skeleton_group_${turnNodeId}`;
      const agent = `skeleton_agent_${turnNodeId}`;
      const child1 = `skeleton_child1_${turnNodeId}`;
      const child2 = `skeleton_child2_${turnNodeId}`;

      // Placeholder group
      nodeDataArray.push({
        key: groupKey,
        isGroup: true,
        location: `${TURN_X + GROUP_X_OFFSET} ${turnY}`,
      });

      // 1 agent skeleton node
      nodeDataArray.push({ key: agent, category: 'skeleton', group: groupKey });

      // 2 child skeleton nodes
      nodeDataArray.push({
        key: child1,
        category: 'skeleton',
        group: groupKey,
      });
      nodeDataArray.push({
        key: child2,
        category: 'skeleton',
        group: groupKey,
      });

      // Turn → agent
      linkDataArray.push({
        id: `skel_turn_a_${turnNodeId}`,
        from: turnNodeId,
        to: agent,
        fromPort: 'right',
        toPort: 'left',
      });

      // Agent → children
      linkDataArray.push({
        id: `skel_a_c1_${turnNodeId}`,
        from: agent,
        to: child1,
        fromPort: 'right',
        toPort: 'left',
      });
      linkDataArray.push({
        id: `skel_a_c2_${turnNodeId}`,
        from: agent,
        to: child2,
        fromPort: 'right',
        toPort: 'left',
      });
    });
  }

  // Pagination placeholder: when a fetchMore is in flight, drop a synthetic
  // 'turn_loader' node in the next-turn slot and chain it from the current
  // last real turn. When the response arrives, narrativeTurnsFetchingMore
  // flips false and this block no-ops, so the new turns occupy the slot.
  if (
    isFetchingMoreTurns &&
    turnsData?.turn_ids &&
    turnsData.turn_ids.length > 0
  ) {
    const lastEntry =
      turnsData.turn_ids[turnsData.turn_ids.length - 1];
    const lastTurnKey: string | undefined = lastEntry
      ? Object.keys(lastEntry)[0]
      : undefined;

    if (lastTurnKey) {
      const placeholderKey = 'turn_loader_placeholder';
      const placeholderY =
        TURN_START_Y + turnsData.turn_ids.length * TURN_VERTICAL_SPACING;

      // Push end nodes down by one slot so the loader can occupy the next-turn
      // slot without overlapping the stop button. Also collect their keys so we
      // can re-route the last_turn -> end edges through the loader.
      const endKeys: string[] = [];
      for (const n of nodeDataArray) {
        if (n.category !== 'end') continue;
        endKeys.push(n.key);
        const pt = go.Point.parse(n.location);
        n.location = go.Point.stringify(
          new go.Point(pt.x, pt.y + TURN_VERTICAL_SPACING)
        );
      }

      // Re-route any existing last_turn -> end edge through the loader so the
      // chain stays connected and doesn't draw diagonally around the loader.
      const endKeySet = new Set(endKeys);
      for (let i = linkDataArray.length - 1; i >= 0; i--) {
        const link = linkDataArray[i];
        if (link.from === lastTurnKey && endKeySet.has(link.to)) {
          linkDataArray.splice(i, 1);
        }
      }

      nodeDataArray.push({
        key: placeholderKey,
        category: 'turn_loader',
        location: `${TURN_X - 14} ${placeholderY - 10}`,
      });

      linkDataArray.push({
        id: `${lastTurnKey}-to-${placeholderKey}`,
        from: lastTurnKey,
        to: placeholderKey,
        fromPort: 'bottom',
        toPort: 'top',
        label: '',
        tooltip: '',
        edgeType: 'sequence',
        edgeLabel: '',
        stepNumber: undefined,
        contextId: undefined,
        isConcurrent: false,
        status: '',
      });

      for (const endKey of endKeys) {
        linkDataArray.push({
          id: `${placeholderKey}-to-${endKey}`,
          from: placeholderKey,
          to: endKey,
          fromPort: 'bottom',
          toPort: 'top',
          label: '',
          tooltip: '',
          edgeType: 'sequence',
          edgeLabel: '',
          stepNumber: undefined,
          contextId: undefined,
          isConcurrent: false,
          status: '',
        });
      }
    }
  }

  const newLinkDataArray = enrichLinkDataWithSpanDetails(
    linkDataArray,
    spanDetails
  );
  const newNodeDataArray = enrichNodeDataWithSpanDetails(
    nodeDataArray,
    spanDetails
  );

  return (
    <div style={{ display: 'flex', width: '100%', height, overflow: 'hidden' }}>
      <ReactDiagram
        ref={diagramRef}
        initDiagram={initDiagram}
        divClassName={className}
        nodeDataArray={newNodeDataArray}
        linkDataArray={newLinkDataArray}
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          backgroundColor: '#08242F',
        }}
      />
      {selectedElement && (
        <DetailPanel selectedElement={selectedElement} onClose={handleClose} />
      )}
    </div>
  );
}
